// cleanup_convert_to_typescript_file_GENERICS
// cleanup_convert_to_glu_component_GENERICS

import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { debounce, padEnd, padStart } from 'lodash';

import useLocale from 'components/useLocale';
import { isBrowserSlow } from 'utils/browserSpeed';

import Input from './Input';
import useStyles from './CurrencyInput.styles';

export const exp = (num, pow) => num ** pow;

export const sanitize = (str) => String(str).replace(/[^0-9]/g, '');

export const checkDecimals = (str) => {
  const regex = new RegExp(/\.\d{1,3}/, 'g');

  const match = String(str).match(regex);
  const hasDecimals = !!match;

  return {
    hasDecimals,
    decimals: match && match[0].slice(1)
  };
};

/**
 * Include decimals in a number
 *
 * @param {string|number} num
 */
const includeDecimals = (num) => {
  const decimalsCount = 2;
  const { decimals } = checkDecimals(num);
  const formattedNum = decimals ? parseFloat(num) * exp(10, decimals.length) : num;

  const amount = parseFloat(formattedNum);
  return ((amount * exp(10, decimalsCount)) / exp(100, 2)).toFixed(2);
};

/**
 * Format number with a separator
 * @param {number|string} num
 */
const formatNumber = (num, separator) => {
  if (!num) {
    return num;
  }

  const { decimals, hasDecimals } = checkDecimals(num);
  const n = String(
    includeDecimals(hasDecimals ? Math.round(num * exp(10, decimals.length)) : num)
  );
  const p = n.indexOf('.');
  return n.replace(/\d(?=(?:\d{3})+(?:\.|$))/g, (m, i) =>
    p < 0 || i < p ? `${m}${separator}` : m
  );
};

const isDeleteEvent = (newVal, prevVal) => {
  const newValLength = newVal.replace('.', '0').replace(/^0*/g, '').length;
  const prevValLength = prevVal ? prevVal.replace('.', '0').replace(/^0*/g, '').length : 0;
  return newValLength < prevValLength || (!newValLength && !prevValLength);
};

const normalizeDecimals = (inputVal) => {
  const defValue = String(inputVal);
  const decimalSplit = defValue.split('.');
  if (decimalSplit.length < 2) return defValue;
  const numDecimals = decimalSplit[1].length || 0;
  if (numDecimals === 2) return defValue;
  return `${decimalSplit[0]}.${padEnd(decimalSplit[1].substring(0, 2), 2, '0')}`;
};

export const CurrencyInputComponent = React.forwardRef(
  (
    {
      className: passedClasses,
      classes: { amountField },
      debounceInput,
      defaultValue,
      maxLength,
      onBlur: validateBlur,
      onChange: validator,
      onFocus: onFocusCallback,
      validateBlurOnly,
      value,
      // Removing so they do not get passed to input
      /* eslint-disable */
      exact,
      resetErrors,
      resetData,
      disabled,
      /* eslint-enable */
      ...props
    },
    ref
  ) => {
    const separator = ',';
    const defValue = defaultValue || value || '';
    const { hasDecimals } = checkDecimals(defValue);

    const [inputValue, setInputValue] = useState(
      !hasDecimals && defValue !== ''
        ? includeDecimals(defValue * 100)
        : normalizeDecimals(defValue)
    );

    const debouncedValidator = useMemo(
      () =>
        debounce((event) => {
          validator(event);
        }, 250),
      [validator]
    );

    // Run onchange the first time to get the 0.00.
    // Then debounce
    const validateChange = (...args) => {
      // Add && debounceInput
      if (inputValue && debounceInput && isBrowserSlow()) {
        debouncedValidator(...args);
        return;
      }
      validator(...args);
    };

    const onChange = (event) => {
      // Sometimes it can happen, when form is improperly handled
      // Multiple updates are coming even with dummy event values
      // We are quiting here, in case when value is "not present"
      // Encountered in: [TaxPayment]
      if (typeof event.target.value === 'undefined') {
        return;
      }

      const currValue = event.target.value || '';
      const { hasDecimals } = checkDecimals(currValue);

      if (!hasDecimals) {
        const updateValue =
          currValue !== '' ? includeDecimals(sanitize(padStart(currValue, '3', '0'))) : currValue;
        validateChange({ target: { value: updateValue } });
        setInputValue(updateValue);
      } else {
        const sanitized = sanitize(currValue);
        const match = sanitized.match(/[0-9]+/g);
        const strLength = match && match.join('').length - 1;
        const value = formatNumber(sanitized, separator);
        const isDelete = isDeleteEvent(value, inputValue);

        if (strLength <= maxLength) {
          const pos = value.startsWith('0.0') ? 5 : event.target.selectionEnd;
          const element = event.target;

          const lengthIncrease =
            value.length > inputValue.length ? value.length - inputValue.length : 0;

          setTimeout(() => {
            const deletePos =
              value.length !== inputValue.length
                ? pos + 1 - (inputValue.length - value.length)
                : pos + 1;
            const newPos = isDelete ? deletePos : pos - 1 + lengthIncrease;

            // special case
            const periodBuffer = !isDelete && value.length - 2 === newPos ? 1 : 0;
            if (element.setSelectionRange)
              element.setSelectionRange(newPos - periodBuffer, newPos - periodBuffer);
          }, 0);
          validateChange({ target: { value } });
          setInputValue(value);
        } else {
          validateChange({ target: { value: inputValue } });
        }
      }
    };

    const onFocus = (event) => {
      if (parseFloat(event.target.value) === 0) onChange(event);
    };

    const onFocusEventFired = (event) => {
      onFocus(event);
      if (onFocusCallback) {
        onFocusCallback(event);
      }
      return null;
    };

    const onBlur = (event) => {
      const value = event.target.value;
      const { hasDecimals } = checkDecimals(value);

      // quit when number is already formatted
      if (hasDecimals) {
        return;
      }

      // if there's no value, use 0 instead
      const updatedValue = (!value && includeDecimals(0)) || value;

      setInputValue(updatedValue);
      validateChange({ target: { value: updatedValue } });
    };

    useEffect(() => {
      if (value !== defaultValue) {
        onChange({ target: {} }, value);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue, value]);

    return (
      <input
        inputMode="numeric"
        style={!props.readOnly ? { textAlign: 'right' } : {}}
        {...props}
        className={`${amountField} ${passedClasses}`}
        onChange={(ev) => onChange(ev)}
        onClick={onFocus}
        onFocus={onFocusEventFired}
        onBlur={onBlur}
        step="0.01"
        value={value ? formatNumber(sanitize(inputValue), separator) : ''}
        ref={ref}
        disabled={disabled}
      />
    );
  }
);

CurrencyInputComponent.displayName = 'CurrencyInputComponent';

CurrencyInputComponent.propTypes = {
  className: PropTypes.string,
  maxLength: PropTypes.number,
  debounceInput: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  readOnly: PropTypes.bool,
  exact: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  validateBlurOnly: PropTypes.bool
};

CurrencyInputComponent.defaultProps = {
  className: '',
  debounceInput: false,
  maxLength: 10,
  onBlur() {},
  onFocus() {},
  onChange() {},
  exact: false,
  value: '',
  defaultValue: '',
  readOnly: false,
  validateBlurOnly: false
};

export const CurrencyValidationComponent = ({ addon, ...props }) => {
  const locale = useLocale();
  const classes = useStyles();
  CurrencyValidationComponent.defaultProps = {
    addon: '$',
    readOnly: false
  };

  CurrencyValidationComponent.propTypes = {
    addon: PropTypes.string,
    readOnly: PropTypes.bool
  };

  return (
    <div aria-hidden="true" data-qa="payment-currency-unit">
      <Input
        aria-label={locale['common.amount']}
        classes={classes}
        {...props}
        rendersInput
        disabled={props.disabled}
        inputComponent={CurrencyInputComponent}
        prepend={
          !props.readOnly ? (
            <span data-qa="input-currency-unit" className="input-group-addon">
              {addon}
            </span>
          ) : null
        }
      />
    </div>
  );
};

export default CurrencyValidationComponent;
